import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { 
  Receipt, 
  CheckCircle, 
  Warning, 
  Error as ErrorIcon,
  TrendingUp,
  CalendarToday,
  AttachMoney
} from "@material-ui/icons";
import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  alertBanner: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    border: '1px solid #ef9a9a',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 8px rgba(198, 40, 40, 0.1)',
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  statHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  statLabel: {
    fontSize: "13px",
    color: "#666",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "4px",
  },
  statSubtext: {
    fontSize: "12px",
    color: "#999",
  },
  invoicesContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
  },
  invoicesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "2px solid #e8ecf1",
  },
  invoicesTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  invoiceCard: {
    background: "#fafbfc",
    borderRadius: "10px",
    padding: "20px",
    marginBottom: "16px",
    border: "1px solid #e8ecf1",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      transform: "translateX(4px)",
    },
  },
  invoiceCardExpired: {
    background: "#fff5f5",
    border: "1px solid #feb2b2",
  },
  invoiceCardPaid: {
    background: "#f0fdf4",
    border: "1px solid #86efac",
  },
  invoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "12px",
  },
  invoiceId: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#666",
  },
  invoiceStatus: {
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusPaid: {
    background: "#d1fae5",
    color: "#065f46",
  },
  statusPending: {
    background: "#fef3c7",
    color: "#92400e",
  },
  statusExpired: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  invoiceDetails: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  invoiceDetail: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  detailLabel: {
    fontSize: "11px",
    color: "#999",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  detailValue: {
    fontSize: "14px",
    color: "#1a1a1a",
    fontWeight: 600,
  },
  invoiceFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
    flexWrap: "wrap",
    gap: "12px",
  },
  invoiceValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#10b981",
  },
  payButton: {
    padding: "10px 24px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      background: "#2563eb",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
    },
  },
  paidButton: {
    padding: "10px 24px",
    background: "#e8ecf1",
    color: "#666",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "not-allowed",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#999",
  },
  loadingState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666",
  },
}));

const Invoices = () => {
  const classes = useStyles();
  const { user, handleLogout } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);

  // Verificar se a empresa está vencida
  useEffect(() => {
    if (user && user.company) {
      const hoje = moment();
      const vencimento = moment(user.company.dueDate);
      const isExpired = hoje.isAfter(vencimento);
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  const handleOpenContactModal = (invoice) => {
    setStoragePlans(invoice);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INVOICES", payload: data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, searchParam ? 250 : 100); // Reduzir delay: 250ms para busca, 100ms para mudanças
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const getInvoiceStatus = (invoice) => {
    const hoje = moment();
    const vencimento = moment(invoice.dueDate);
    const diff = vencimento.diff(hoje, 'days');
    
    if (invoice.status === "paid") return "paid";
    if (diff < 0) return "expired";
    return "pending";
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "paid": return "Pago";
      case "expired": return "Vencido";
      case "pending": return "Em Aberto";
      default: return "Desconhecido";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid": return <CheckCircle style={{ fontSize: "20px" }} />;
      case "expired": return <ErrorIcon style={{ fontSize: "20px" }} />;
      case "pending": return <Warning style={{ fontSize: "20px" }} />;
      default: return null;
    }
  };

  // Calcular estatísticas
  const stats = {
    total: invoices.length,
    paid: invoices.filter(inv => getInvoiceStatus(inv) === "paid").length,
    pending: invoices.filter(inv => getInvoiceStatus(inv) === "pending").length,
    expired: invoices.filter(inv => getInvoiceStatus(inv) === "expired").length,
    totalValue: invoices.reduce((sum, inv) => sum + inv.value, 0),
  };

  return (
    <div className={classes.container}>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}
      />

      {/* Header */}
      <div className={classes.header}>
        <h1 className={classes.title}>💰 Financeiro</h1>
        <button
          onClick={() => {
            handleLogout();
          }}
          style={{
            padding: "10px 20px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#dc2626";
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 2px 8px rgba(239, 68, 68, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#ef4444";
            e.target.style.transform = "none";
            e.target.style.boxShadow = "none";
          }}
        >
          Sair
        </button>
      </div>

      {/* Alerta de Vencimento */}
      {isCompanyExpired && (
        <div className={classes.alertBanner}>
          <ErrorIcon style={{ fontSize: "24px" }} />
          <div>
            <strong>Atenção!</strong> Sua assinatura está vencida. Entre em contato com o suporte para regularizar sua situação.
          </div>
        </div>
      )}

      {/* Estatísticas */}
      <div className={classes.statsGrid}>
        <div className={classes.statCard}>
          <div className={classes.statHeader}>
            <span className={classes.statLabel}>Total de Faturas</span>
            <div className={classes.statIcon} style={{ background: "#dbeafe" }}>
              <Receipt style={{ color: "#3b82f6", fontSize: "20px" }} />
            </div>
          </div>
          <div className={classes.statValue}>{stats.total}</div>
          <div className={classes.statSubtext}>Todas as faturas</div>
        </div>

        <div className={classes.statCard}>
          <div className={classes.statHeader}>
            <span className={classes.statLabel}>Pagas</span>
            <div className={classes.statIcon} style={{ background: "#d1fae5" }}>
              <CheckCircle style={{ color: "#10b981", fontSize: "20px" }} />
            </div>
          </div>
          <div className={classes.statValue} style={{ color: "#10b981" }}>{stats.paid}</div>
          <div className={classes.statSubtext}>Faturas quitadas</div>
        </div>

        <div className={classes.statCard}>
          <div className={classes.statHeader}>
            <span className={classes.statLabel}>Pendentes</span>
            <div className={classes.statIcon} style={{ background: "#fef3c7" }}>
              <Warning style={{ color: "#f59e0b", fontSize: "20px" }} />
            </div>
          </div>
          <div className={classes.statValue} style={{ color: "#f59e0b" }}>{stats.pending}</div>
          <div className={classes.statSubtext}>Aguardando pagamento</div>
        </div>

        <div className={classes.statCard}>
          <div className={classes.statHeader}>
            <span className={classes.statLabel}>Vencidas</span>
            <div className={classes.statIcon} style={{ background: "#fee2e2" }}>
              <ErrorIcon style={{ color: "#ef4444", fontSize: "20px" }} />
            </div>
          </div>
          <div className={classes.statValue} style={{ color: "#ef4444" }}>{stats.expired}</div>
          <div className={classes.statSubtext}>Faturas atrasadas</div>
        </div>

        <div className={classes.statCard}>
          <div className={classes.statHeader}>
            <span className={classes.statLabel}>Valor Total</span>
            <div className={classes.statIcon} style={{ background: "#e0e7ff" }}>
              <TrendingUp style={{ color: "#6366f1", fontSize: "20px" }} />
            </div>
          </div>
          <div className={classes.statValue} style={{ color: "#6366f1", fontSize: "24px" }}>
            {stats.totalValue.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
          </div>
          <div className={classes.statSubtext}>Soma de todas as faturas</div>
        </div>
      </div>

      {/* Lista de Faturas */}
      <div className={classes.invoicesContainer}>
        <div className={classes.invoicesHeader}>
          <h2 className={classes.invoicesTitle}>
            <Receipt />
            Suas Faturas ({invoices.length})
          </h2>
        </div>

        {loading ? (
          <div className={classes.loadingState}>
            <p>⏳ Carregando faturas...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className={classes.emptyState}>
            <Receipt style={{ fontSize: "64px", color: "#ddd", marginBottom: "16px" }} />
            <p>Nenhuma fatura encontrada</p>
          </div>
        ) : (
          invoices.map((invoice) => {
            const status = getInvoiceStatus(invoice);
            const cardClass = status === "expired" 
              ? `${classes.invoiceCard} ${classes.invoiceCardExpired}`
              : status === "paid"
              ? `${classes.invoiceCard} ${classes.invoiceCardPaid}`
              : classes.invoiceCard;

            return (
              <div key={invoice.id} className={cardClass}>
                <div className={classes.invoiceHeader}>
                  <div className={classes.invoiceId}>
                    Fatura #{invoice.id} - {invoice.detail}
                  </div>
                  <div 
                    className={`${classes.invoiceStatus} ${
                      status === "paid" 
                        ? classes.statusPaid 
                        : status === "expired"
                        ? classes.statusExpired
                        : classes.statusPending
                    }`}
                  >
                    {getStatusIcon(status)} {getStatusLabel(status)}
                  </div>
                </div>

                <div className={classes.invoiceDetails}>
                  <div className={classes.invoiceDetail}>
                    <span className={classes.detailLabel}>Usuários</span>
                    <span className={classes.detailValue}>{invoice.users}</span>
                  </div>
                  <div className={classes.invoiceDetail}>
                    <span className={classes.detailLabel}>Conexões</span>
                    <span className={classes.detailValue}>{invoice.connections}</span>
                  </div>
                  <div className={classes.invoiceDetail}>
                    <span className={classes.detailLabel}>Filas</span>
                    <span className={classes.detailValue}>{invoice.queues}</span>
                  </div>
                  <div className={classes.invoiceDetail}>
                    <span className={classes.detailLabel}>
                      <CalendarToday style={{ fontSize: "12px", marginRight: "4px" }} />
                      Vencimento
                    </span>
                    <span className={classes.detailValue}>
                      {moment(invoice.dueDate).format("DD/MM/YYYY")}
                    </span>
                  </div>
                </div>

                <div className={classes.invoiceFooter}>
                  <div className={classes.invoiceValue}>
                    {invoice.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                  </div>
                  {status !== "paid" ? (
                    <button
                      className={classes.payButton}
                      onClick={() => handleOpenContactModal(invoice)}
                    >
                      <AttachMoney style={{ fontSize: "18px" }} />
                      Pagar Agora
                    </button>
                  ) : (
                    <button className={classes.paidButton}>
                      <CheckCircle style={{ fontSize: "18px", marginRight: "6px" }} />
                      Pago
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Invoices;
